import React, { Dispatch, FC, SetStateAction, useCallback, useState } from 'react';
import { ReCAPTCHA } from 'react-google-recaptcha';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  FormGroup,
  FormText,
  Input,
  Label,
  Row,
} from 'reactstrap';
import { BaseCreateUser, CreateUser } from '../../../../shared/user/create';
import { getDob, StripeIdentityUser } from '../../../../shared/user/identity';
import { passwordScore } from '../../../../shared/user/password';
import { PasswordChangeInput, RegisterUserInfo, UniqueAttributeInput } from '../../../components';
import { ActionType } from '../../../services/ActionTypes';
import { useTranslation } from '../../../translations';
import {
  useConfig,
  useConvention,
  useForm,
  useInputCheckboxModel,
  useObject,
} from '../../../utils';
import { getConventionIdParam } from '../getConventionIdParam';

interface RegisterFormProps {
  readonly onComplete: () => void;
  readonly forceValues?: StripeIdentityUser;
  readonly identityId?: string;
}

export const RegisterForm: FC<RegisterFormProps> = ({ onComplete, forceValues, identityId }) => {
  const { ts } = useTranslation();
  const history = useHistory();
  const convention = useConvention();
  const {
    captcha,
    organization: { country },
  } = useConfig();

  const dispatch = useDispatch();
  const [user, setUser] = useState<CreateUser>({
    firstName: forceValues?.firstName ?? '',
    lastName: forceValues?.lastName ?? '',
    bornAt: forceValues ? getDob(forceValues) : undefined,
    password: '',
    username: '',
    identityId,
    conventionId: getConventionIdFromParam(),
    newsletter: true,
    addressCountry: country,
  });

  const [, setUsername] = useObject(user, setUser, 'username');
  const [, setEmail] = useObject(user, setUser, 'email');
  const [, setPassword] = useObject(user, setUser, 'password');
  const setNewsletter = useInputCheckboxModel(setUser, 'newsletter');

  const form = useForm(async () => {
    const data = convention.registrationRequirePii
      ? await api.createUser(user)
      : await api.createUser({
          username: user.username,
          email: user.email,
          password: user.password,
          newsletter: user.newsletter,
          conventionId: user.conventionId,
        });

    if (data.verified) {
      const activeUser = await api.getActiveUser();
      dispatch({ type: ActionType.LoginSuccess, user: activeUser });
      history.push(`/login${getConventionIdParam()}`);
    } else {
      onComplete();
    }
  }, [convention.registrationRequirePii, dispatch, history, onComplete, user]);

  return (
    <Col lg={6} xs={12}>
      <Card style={{ marginBottom: '100px' }}>
        <CardBody className="text-center">
          <div id="step3Section">
            <CardTitle>{ts('account_creation')}</CardTitle>
            <hr />
            <form onSubmit={form.onSubmit}>
              <FormGroup id="usernameGroup" row>
                <Label for="username" sm={4}>
                  {ts('common.username')}
                </Label>
                <Col sm={8}>
                  <UniqueAttributeInput onChange={setUsername} type="username" />
                  <FormText color="muted">{ts('your_username_is_an_identifier')}</FormText>
                </Col>
              </FormGroup>
              <FormGroup id="emailGroup" row>
                <Label for="email" sm={4}>
                  {ts('common.email_address')}
                </Label>
                <Col sm={8}>
                  <UniqueAttributeInput onChange={setEmail} type="email" />
                </Col>
              </FormGroup>
              <FormGroup id="newsletterGroup" row>
                <Col sm={4} />
                <Col sm={8}>
                  <FormGroup check className="mt-10 text-start">
                    <Input
                      checked={user.newsletter}
                      id="newsletter"
                      name="newsletter"
                      onChange={setNewsletter}
                      type="checkbox"
                    />
                    <Label check for="newsletter">
                      {ts('i_want_to_receive_the', convention.longName)}
                    </Label>
                  </FormGroup>
                </Col>
              </FormGroup>
              <PasswordChangeInput onChange={setPassword} passwordScore={passwordScore} sm={8} />
              <RegistrationPii forceValues={!!forceValues} setUser={setUser} user={user} />
              {captcha?.enabled && (
                <>
                  <hr />
                  <FormGroup className="justify-content-center" row>
                    <ReCAPTCHA
                      onChange={() => {
                        /* noop */
                      }}
                      sitekey={captcha.siteKey}
                      size="normal"
                    />
                  </FormGroup>
                </>
              )}
              <hr />
              <Row className="justify-content-center">
                <Col className="mt-10" lg={6} xs={12}>
                  <Button block color="primary" id="regFormSubmit" type="submit">
                    {ts('login.create_your_account')}
                  </Button>
                </Col>
              </Row>
            </form>
          </div>
        </CardBody>
      </Card>
    </Col>
  );
};

interface RegistrationPiiProps {
  readonly forceValues?: boolean;
  readonly user: CreateUser;
  readonly setUser: Dispatch<SetStateAction<CreateUser>>;
}

const RegistrationPii: FC<RegistrationPiiProps> = ({ forceValues, user, setUser }) => {
  const conv = useConvention();
  const updateUser = useCallback(
    (newUser: SetStateAction<BaseCreateUser>) => {
      if (typeof newUser === 'function') {
        setUser((oldUser) => ({ ...oldUser, ...newUser(oldUser) }));
      } else {
        setUser({ ...user, ...newUser });
      }
    },
    [user, setUser],
  );

  if (conv.registrationRequirePii) {
    return (
      <>
        <hr />
        <RegisterUserInfo forceValues={forceValues} setUser={updateUser} user={user} />
      </>
    );
  }

  return null;
};

export function getConventionIdFromParam(): number | undefined {
  const params = new URLSearchParams(window.location.search);
  const conventionId = Number.parseInt(params.get('conventionId') ?? '', 10);
  if (!Number.isNaN(conventionId)) {
    return conventionId;
  }

  return undefined;
}
